import { Component } from '@theme/component';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * ENDURE CART DRAWER
 *
 * Handles:
 * - Cart drawer lifecycle
 * - Auto open after add-to-cart
 * - Sticky summary
 * - Search & Discovery complementary products
 * - Empty cart recommendations
 * - Last viewed product recommendations
 * - Best seller fallback
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {MutationObserver | null} */
  #recommendationsObserver = null;

  /** @type {AbortController | null} */
  #recommendationsAbortController = null;

  /** @type {number | null} */
  #recommendationsTimer = null;

  /** @type {boolean} */
  #isRenderingRecommendations = false;

  /** @type {string} */
  #lastRecommendationSignature = '';

  /** @type {string} */
  #lastViewedStorageKey = 'endure:last-viewed-product-id';


  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (
      this.closest('theme-drawer')
    );
  }


  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }


  /* ============================================================
     CONNECT
     ============================================================ */

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    this.#themeDrawer?.addEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );

    this.addEventListener(
      'click',
      this.#handleRecommendationClick
    );

    this.addEventListener(
      'change',
      this.#handleRecommendationVariantChange
    );

    this.#ensureRecommendationStyles();

    /*
      Save current product as last viewed product.
    */
    this.#rememberCurrentProduct();


    /*
      Watch cart drawer HTML updates.
      CartItemsComponent can replace content after
      quantity / remove / add operations.
    */
    this.#recommendationsObserver =
      new MutationObserver(() => {
        if (this.#isRenderingRecommendations) {
          return;
        }

        this.#queueRecommendationsLoad();
      });


    this.#recommendationsObserver.observe(
      this,
      {
        childList: true,
        subtree: true
      }
    );


    /*
      Drawer may already be open before module loads.
    */
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }


  /* ============================================================
     DISCONNECT
     ============================================================ */

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    this.#themeDrawer?.removeEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );

    this.removeEventListener(
      'click',
      this.#handleRecommendationClick
    );

    this.removeEventListener(
      'change',
      this.#handleRecommendationVariantChange
    );

    this.#recommendationsObserver?.disconnect();

    this.#recommendationsObserver = null;

    this.#recommendationsAbortController?.abort();

    this.#recommendationsAbortController = null;

    if (this.#recommendationsTimer) {
      window.clearTimeout(
        this.#recommendationsTimer
      );

      this.#recommendationsTimer = null;
    }
  }


  /* ============================================================
     DRAWER OPEN
     ============================================================ */

  #handleDrawerOpen = () => {
    this.#updateStickyState();

    /*
      Load recommendations even if cart is empty.
    */
    this.#queueRecommendationsLoad(true);


    /*
      Close cart drawer when installments dialog opens.
    */
    customElements
      .whenDefined('shopify-payment-terms')
      .then(() => {
        const cta =
          this
            .querySelector('shopify-payment-terms')
            ?.shadowRoot
            ?.querySelector('#shopify-installments-cta');

        cta?.addEventListener(
          'click',
          () => this.#themeDrawer?.close(),
          {
            once: true
          }
        );
      });
  };


  /* ============================================================
     CART UPDATE
     ============================================================ */

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen =
      this.hasAttribute('auto-open') &&
      event.action === 'add' &&
      !this.#themeDrawer?.isOpen;


    const sourceModal =
      /** @type {HTMLDialogElement | null} */ (
        event.target instanceof Element
          ? event.target.closest('dialog:modal')
          : null
      );


    if (
      shouldAutoOpen &&
      !sourceModal &&
      !this.#isCartEmpty()
    ) {
      this.#themeDrawer?.open();
    }


    event.promise
      ?.then(({ detail }) => {
        const settle = () => {
          requestAnimationFrame(() => {
            this.#updateStickyState();

            /*
              Wait for cart HTML to finish rendering.
            */
            window.setTimeout(() => {
              this.#lastRecommendationSignature = '';

              this.#queueRecommendationsLoad(true);
            }, 100);
          });
        };


        if (
          !shouldAutoOpen ||
          detail?.didError
        ) {
          settle();

          return;
        }


        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) {
            this.#themeDrawer?.open();
          }

          settle();
        };


        if (sourceModal?.open) {
          sourceModal.addEventListener(
            'close',
            openAndSettle,
            {
              once: true
            }
          );
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[cart-drawer] Event promise rejected:',
            error
          );
        }
      });
  };


  /* ============================================================
     CART STATE
     ============================================================ */

  #isCartEmpty() {
    return Boolean(
      this.querySelector(
        '.cart-drawer--empty'
      )
    );
  }


  /* ============================================================
     STICKY SUMMARY
     ============================================================ */

  #updateStickyState() {
    const dialog =
      this.#dialog;

    if (!dialog) {
      return;
    }


    const content =
      dialog.querySelector(
        '.cart-drawer__content'
      );


    const summary =
      dialog.querySelector(
        '.cart-drawer__summary'
      );


    if (
      !content ||
      !summary
    ) {
      dialog.setAttribute(
        'cart-summary-sticky',
        'false'
      );

      return;
    }


    const drawerHeight =
      dialog
        .getBoundingClientRect()
        .height;


    const summaryHeight =
      summary
        .getBoundingClientRect()
        .height;


    const ratio =
      summaryHeight /
      drawerHeight;


    dialog.setAttribute(
      'cart-summary-sticky',
      ratio > this.#summaryThreshold
        ? 'false'
        : 'true'
    );
  }


  /* ============================================================
     CURRENT PRODUCT
     ============================================================ */

  #getCurrentProductHandle() {
    const match =
      window.location.pathname.match(
        /\/products\/([^/?#]+)/
      );


    if (
      !match ||
      !match[1]
    ) {
      return null;
    }


    try {
      return decodeURIComponent(
        match[1]
      );
    } catch {
      return match[1];
    }
  }


  /* ============================================================
     CURRENT PRODUCT ID
     ============================================================ */

  async #getCurrentProductId(
    signal = undefined
  ) {
    const handle =
      this.#getCurrentProductHandle();


    if (!handle) {
      return null;
    }


    const root =
      window.Shopify?.routes?.root ||
      '/';


    try {
      const response =
        await fetch(
          `${root}products/${encodeURIComponent(handle)}.js`,
          {
            headers: {
              Accept: 'application/json'
            },
            signal
          }
        );


      if (!response.ok) {
        return null;
      }


      const product =
        await response.json();


      if (!product?.id) {
        return null;
      }


      return String(
        product.id
      );
    } catch (error) {
      if (error?.name === 'AbortError') {
        throw error;
      }

      return null;
    }
  }


  /* ============================================================
     REMEMBER CURRENT PRODUCT
     ============================================================ */

  async #rememberCurrentProduct() {
    try {
      const productId =
        await this.#getCurrentProductId();


      if (!productId) {
        return;
      }


      window.localStorage.setItem(
        this.#lastViewedStorageKey,
        productId
      );
    } catch {
      /*
        localStorage may be unavailable in
        some privacy / preview environments.
      */
    }
  }


  /* ============================================================
     LAST VIEWED PRODUCT
     ============================================================ */

  #getLastViewedProductId() {
    try {
      return window.localStorage.getItem(
        this.#lastViewedStorageKey
      );
    } catch {
      return null;
    }
  }


  /* ============================================================
     EMPTY CART SOURCE PRODUCT
     ============================================================ */

  async #getEmptyCartSourceProductId(
    signal
  ) {
    /*
      Priority 1:
      current product page.
    */
    const currentProductId =
      await this.#getCurrentProductId(
        signal
      );


    if (currentProductId) {
      try {
        window.localStorage.setItem(
          this.#lastViewedStorageKey,
          currentProductId
        );
      } catch {
        //
      }

      return currentProductId;
    }


    /*
      Priority 2:
      last product viewed.
    */
    return this.#getLastViewedProductId();
  }


  /* ============================================================
     QUEUE RECOMMENDATIONS
     ============================================================ */

  #queueRecommendationsLoad(
    force = false
  ) {
    if (this.#recommendationsTimer) {
      window.clearTimeout(
        this.#recommendationsTimer
      );
    }


    this.#recommendationsTimer =
      window.setTimeout(() => {
        this.#recommendationsTimer = null;

        this.#loadRecommendations(
          force
        );
      }, 100);
  }


  /* ============================================================
     LOAD RECOMMENDATIONS
     ============================================================ */

  async #loadRecommendations(
    force = false
  ) {
    const content =
      this.querySelector(
        '.cart-drawer__content'
      );


    if (!content) {
      return;
    }


    this
      .#recommendationsAbortController
      ?.abort();


    this.#recommendationsAbortController =
      new AbortController();


    const {
      signal
    } =
      this.#recommendationsAbortController;


    const root =
      window.Shopify?.routes?.root ||
      '/';


    try {
      /* =========================
         CART
         ========================= */

      const cartResponse =
        await fetch(
          `${root}cart.js`,
          {
            headers: {
              Accept: 'application/json'
            },
            signal
          }
        );


      if (!cartResponse.ok) {
        throw new Error(
          'Could not load cart'
        );
      }


      const cart =
        await cartResponse.json();


      const items =
        Array.isArray(
          cart?.items
        )
          ? cart.items
          : [];


      const isEmpty =
        items.length === 0;


      const limit =
        4;


      const recommendations =
        [];


      const seenProductIds =
        new Set();


      let signature =
        '';


      /* ========================================================
         CART HAS PRODUCTS
         ======================================================== */

      if (!isEmpty) {
        const cartProductIds =
          [
            ...new Set(
              items
                .map((item) =>
                  String(
                    item?.product_id ||
                    ''
                  )
                )
                .filter(Boolean)
            )
          ];


        cartProductIds.forEach(
          (id) => {
            seenProductIds.add(id);
          }
        );


        signature =
          `cart:${cartProductIds.join(',')}`;


        if (
          !force &&
          signature ===
            this.#lastRecommendationSignature
        ) {
          return;
        }


        /*
          Check recommendations for up to
          first 3 products in cart.
        */
        const sourceProducts =
          cartProductIds.slice(
            0,
            3
          );


        for (
          const productId
          of sourceProducts
        ) {
          if (
            recommendations.length >=
            limit
          ) {
            break;
          }


          const products =
            await this.#fetchComplementaryProducts(
              productId,
              limit,
              signal
            );


          this.#collectProducts(
            recommendations,
            products,
            seenProductIds,
            limit
          );
        }
      }


      /* ========================================================
         EMPTY CART
         ======================================================== */

      else {
        const sourceProductId =
          await this.#getEmptyCartSourceProductId(
            signal
          );


        signature =
          sourceProductId
            ? `empty:${sourceProductId}`
            : 'empty:fallback';


        if (
          !force &&
          signature ===
            this.#lastRecommendationSignature
        ) {
          return;
        }


        /*
          Don't recommend the current /
          last viewed product itself.
        */
        if (sourceProductId) {
          seenProductIds.add(
            String(
              sourceProductId
            )
          );


          /*
            Search & Discovery
            complementary products first.
          */
          const complementary =
            await this.#fetchComplementaryProducts(
              sourceProductId,
              limit,
              signal
            );


          this.#collectProducts(
            recommendations,
            complementary,
            seenProductIds,
            limit
          );
        }
      }


      /* ========================================================
         FALLBACK

         Fill missing slots from Best Seller.
         Works for:
         - Empty cart with no history
         - Product without complementary items
         - Not enough complementary items
         ======================================================== */

      if (
        recommendations.length <
        limit
      ) {
        const fallbackProducts =
          await this.#fetchFallbackProducts(
            8,
            signal
          );


        this.#collectProducts(
          recommendations,
          fallbackProducts,
          seenProductIds,
          limit
        );
      }


      if (
        signal.aborted ||
        !this.isConnected
      ) {
        return;
      }


      this.#lastRecommendationSignature =
        signature;


      if (!recommendations.length) {
        this.#removeRecommendations();

        return;
      }


      this.#renderRecommendations(
        recommendations,
        isEmpty
      );
    } catch (error) {
      if (
        error?.name ===
        'AbortError'
      ) {
        return;
      }


      console.warn(
        '[cart-drawer] Recommendations failed:',
        error
      );


      this.#removeRecommendations();
    }
  }


  /* ============================================================
     SEARCH & DISCOVERY
     ============================================================ */

  async #fetchComplementaryProducts(
    productId,
    limit,
    signal
  ) {
    if (!productId) {
      return [];
    }


    const root =
      window.Shopify?.routes?.root ||
      '/';


    try {
      const url =
        `${root}recommendations/products.json` +
        `?product_id=${encodeURIComponent(productId)}` +
        `&limit=${limit}` +
        `&intent=complementary`;


      const response =
        await fetch(
          url,
          {
            headers: {
              Accept: 'application/json'
            },
            signal
          }
        );


      if (!response.ok) {
        return [];
      }


      const data =
        await response.json();


      return Array.isArray(
        data?.products
      )
        ? data.products
        : [];
    } catch (error) {
      if (
        error?.name ===
        'AbortError'
      ) {
        throw error;
      }

      return [];
    }
  }


  /* ============================================================
     FALLBACK PRODUCTS
     ============================================================ */

  async #fetchFallbackProducts(
    limit,
    signal
  ) {
    const root =
      window.Shopify?.routes?.root ||
      '/';


    /*
      First try ENDURE Best Seller collection.
    */
    const urls = [
      `${root}collections/best-seller/products.json?limit=${limit}`,
      `${root}collections/all/products.json?limit=${limit}`
    ];


    for (const url of urls) {
      try {
        const response =
          await fetch(
            url,
            {
              headers: {
                Accept: 'application/json'
              },
              signal
            }
          );


        if (!response.ok) {
          continue;
        }


        const data =
          await response.json();


        const products =
          Array.isArray(
            data?.products
          )
            ? data.products
            : [];


        if (products.length) {
          return products;
        }
      } catch (error) {
        if (
          error?.name ===
          'AbortError'
        ) {
          throw error;
        }
      }
    }


    return [];
  }


  /* ============================================================
     COLLECT PRODUCTS
     ============================================================ */

  #collectProducts(
    target,
    products,
    seenProductIds,
    limit
  ) {
    if (!Array.isArray(products)) {
      return;
    }


    for (const product of products) {
      if (
        target.length >=
        limit
      ) {
        break;
      }


      const productId =
        String(
          product?.id ??
          ''
        );


      if (
        !productId ||
        seenProductIds.has(
          productId
        )
      ) {
        continue;
      }


      const variants =
        this.#getAvailableVariants(
          product
        );


      if (!variants.length) {
        continue;
      }


      seenProductIds.add(
        productId
      );


      target.push(
        product
      );
    }
  }


  /* ============================================================
     AVAILABLE VARIANTS
     ============================================================ */

  #getAvailableVariants(
    product
  ) {
    if (
      !Array.isArray(
        product?.variants
      )
    ) {
      return [];
    }


    return product.variants.filter(
      (variant) =>
        variant?.available !== false
    );
  }


  /* ============================================================
     RENDER SECTION
     ============================================================ */

  #renderRecommendations(
    products,
    isEmptyCart
  ) {
    this.#isRenderingRecommendations =
      true;


    const content =
      this.querySelector(
        '.cart-drawer__content'
      );


    if (!content) {
      this.#isRenderingRecommendations =
        false;

      return;
    }


    let section =
      /** @type {HTMLElement | null} */ (
        this.querySelector(
          '[data-endure-cart-recommendations]'
        )
      );


    if (!section) {
      section =
        document.createElement(
          'section'
        );


      section.className =
        'endure-cart-recommendations';


      section.setAttribute(
        'data-endure-cart-recommendations',
        ''
      );


      section.setAttribute(
        'dir',
        'rtl'
      );


      section.setAttribute(
        'aria-label',
        'منتجات مقترحة'
      );


      section.innerHTML = `

        <div class="endure-cart-recommendations__header">

          <div>

            <h3
              class="endure-cart-recommendations__title"
              data-endure-rec-title
            ></h3>

            <p
              class="endure-cart-recommendations__subtitle"
              data-endure-rec-subtitle
            ></p>

          </div>

        </div>


        <div
          class="endure-cart-recommendations__track"
          data-endure-recommendations-track
        ></div>


        <p
          class="endure-cart-recommendations__status"
          data-endure-recommendations-status
          role="status"
          aria-live="polite"
        ></p>

      `;
    }


    section.classList.toggle(
      'endure-cart-recommendations--empty',
      isEmptyCart
    );


    const title =
      section.querySelector(
        '[data-endure-rec-title]'
      );


    const subtitle =
      section.querySelector(
        '[data-endure-rec-subtitle]'
      );


    if (title) {
      title.textContent =
        isEmptyCart
          ? 'ابدأ من هنا'
          : 'كمّل اللوك';
    }


    if (subtitle) {
      subtitle.textContent =
        isEmptyCart
          ? 'اختيارات ممكن تعجبك'
          : 'اختيارات تناسب اللي في سلتك';
    }


    const track =
      section.querySelector(
        '[data-endure-recommendations-track]'
      );


    const status =
      section.querySelector(
        '[data-endure-recommendations-status]'
      );


    if (track) {
      track.innerHTML =
        products
          .map((product) =>
            this.#renderRecommendationCard(
              product
            )
          )
          .join('');
    }


    if (status) {
      status.textContent =
        '';
    }


    section.hidden =
      false;


    /* ==========================================================
       POSITION
       ========================================================== */

    if (isEmptyCart) {
      /*
        Empty cart has no summary.
        Put recommendations after empty
        cart items/message.
      */

      const emptyItems =
        content.querySelector(
          '.cart-drawer__items'
        );


      if (emptyItems) {
        emptyItems.after(
          section
        );
      } else {
        content.append(
          section
        );
      }
    } else {
      /*
        Normal cart:
        recommendations before checkout summary.
      */

      const summary =
        content.querySelector(
          '.cart-drawer__summary'
        );


      if (summary) {
        summary.before(
          section
        );
      } else {
        content.append(
          section
        );
      }
    }


    requestAnimationFrame(() => {
      this.#updateStickyState();

      window.setTimeout(() => {
        this.#isRenderingRecommendations =
          false;
      }, 0);
    });
  }


  /* ============================================================
     REMOVE RECOMMENDATIONS
     ============================================================ */

  #removeRecommendations() {
    const section =
      this.querySelector(
        '[data-endure-cart-recommendations]'
      );


    if (!section) {
      return;
    }


    this.#isRenderingRecommendations =
      true;


    section.remove();


    window.setTimeout(() => {
      this.#isRenderingRecommendations =
        false;
    }, 0);
  }


  /* ============================================================
     PRODUCT CARD
     ============================================================ */

  #renderRecommendationCard(
    product
  ) {
    const variants =
      this.#getAvailableVariants(
        product
      );


    if (!variants.length) {
      return '';
    }


    const firstVariant =
      variants[0];


    const hasMultipleVariants =
      variants.length > 1;


    const title =
      this.#escapeHtml(
        product?.title ||
        ''
      );


    const root =
      window.Shopify?.routes?.root ||
      '/';


    let productUrl =
      product?.url ||
      '';


    if (
      !productUrl &&
      product?.handle
    ) {
      productUrl =
        `${root}products/${product.handle}`;
    }


    const url =
      this.#escapeAttribute(
        productUrl ||
        '#'
      );


    const imageUrl =
      this.#getProductImage(
        product
      );


    const price =
      Number(
        firstVariant?.price ??
        product?.price ??
        0
      );


    const compareAtPrice =
      Number(
        firstVariant?.compare_at_price ??
        product?.compare_at_price ??
        0
      );


    const imageMarkup =
      imageUrl
        ? `
          <img
            class="endure-cart-rec-card__image"
            src="${this.#escapeAttribute(imageUrl)}"
            alt="${this.#escapeAttribute(product?.title || '')}"
            loading="lazy"
            width="320"
            height="400"
          >
        `
        : `
          <span
            class="endure-cart-rec-card__image-placeholder"
            aria-hidden="true"
          ></span>
        `;


    const compareMarkup =
      compareAtPrice > price
        ? `
          <s
            class="endure-cart-rec-card__compare"
            data-endure-rec-compare
          >
            ${this.#formatMoney(compareAtPrice)}
          </s>
        `
        : `
          <s
            class="endure-cart-rec-card__compare"
            data-endure-rec-compare
            hidden
          ></s>
        `;


    /* ==========================================================
       VARIANT SELECT
       ========================================================== */

    const variantSelector =
      hasMultipleVariants
        ? `
          <select
            class="endure-cart-rec-card__select"
            data-endure-rec-variant
            aria-label="اختار المقاس"
          >

            ${variants
              .map((variant) => {
                const variantTitle =
                  this.#escapeHtml(
                    variant?.public_title ||
                    variant?.title ||
                    'اختيار'
                  );


                return `
                  <option
                    value="${this.#escapeAttribute(
                      String(
                        variant?.id ||
                        ''
                      )
                    )}"
                    data-price="${this.#escapeAttribute(
                      String(
                        variant?.price ??
                        0
                      )
                    )}"
                    data-compare-at="${this.#escapeAttribute(
                      String(
                        variant?.compare_at_price ??
                        0
                      )
                    )}"
                  >
                    ${variantTitle}
                  </option>
                `;
              })
              .join('')}

          </select>
        `
        : '';


    return `

      <article
        class="endure-cart-rec-card"
        data-endure-rec-card
      >

        <a
          class="endure-cart-rec-card__image-link"
          href="${url}"
        >

          <div class="endure-cart-rec-card__image-wrap">

            ${imageMarkup}

          </div>

        </a>


        <div class="endure-cart-rec-card__body">

          <a
            class="endure-cart-rec-card__title"
            href="${url}"
          >
            ${title}
          </a>


          <div class="endure-cart-rec-card__price-row">

            <span
              class="endure-cart-rec-card__price"
              data-endure-rec-price
            >
              ${this.#formatMoney(price)}
            </span>

            ${compareMarkup}

          </div>


          ${variantSelector}


          <button
            class="endure-cart-rec-card__add"
            type="button"
            data-endure-recommendation-add
            data-variant-id="${this.#escapeAttribute(
              String(
                firstVariant?.id ||
                ''
              )
            )}"
          >
            ${
              hasMultipleVariants
                ? 'اختار وأضف'
                : 'أضف للسلة'
            }
          </button>

        </div>

      </article>

    `;
  }


  /* ============================================================
     PRODUCT IMAGE
     ============================================================ */

  #getProductImage(
    product
  ) {
    const featuredImage =
      product?.featured_image;


    if (
      typeof featuredImage ===
      'string'
    ) {
      return featuredImage;
    }


    if (
      featuredImage?.url
    ) {
      return featuredImage.url;
    }


    if (
      featuredImage?.src
    ) {
      return featuredImage.src;
    }


    const firstImage =
      product?.images?.[0];


    if (
      typeof firstImage ===
      'string'
    ) {
      return firstImage;
    }


    if (
      firstImage?.url
    ) {
      return firstImage.url;
    }


    if (
      firstImage?.src
    ) {
      return firstImage.src;
    }


    return '';
  }


  /* ============================================================
     VARIANT CHANGE
     ============================================================ */

  #handleRecommendationVariantChange =
    (event) => {
      if (
        !(
          event.target
          instanceof HTMLSelectElement
        )
      ) {
        return;
      }


      if (
        !event.target.matches(
          '[data-endure-rec-variant]'
        )
      ) {
        return;
      }


      const card =
        event.target.closest(
          '[data-endure-rec-card]'
        );


      if (!card) {
        return;
      }


      const selected =
        event.target
          .selectedOptions
          ?.[0];


      if (!selected) {
        return;
      }


      const button =
        /** @type {HTMLButtonElement | null} */ (
          card.querySelector(
            '[data-endure-recommendation-add]'
          )
        );


      const priceElement =
        card.querySelector(
          '[data-endure-rec-price]'
        );


      const compareElement =
        /** @type {HTMLElement | null} */ (
          card.querySelector(
            '[data-endure-rec-compare]'
          )
        );


      const price =
        Number(
          selected.dataset.price ||
          0
        );


      const compareAt =
        Number(
          selected.dataset.compareAt ||
          0
        );


      if (button) {
        button.dataset.variantId =
          selected.value;
      }


      if (priceElement) {
        priceElement.textContent =
          this.#formatMoney(
            price
          );
      }


      if (compareElement) {
        if (
          compareAt > price
        ) {
          compareElement.textContent =
            this.#formatMoney(
              compareAt
            );

          compareElement.hidden =
            false;
        } else {
          compareElement.textContent =
            '';

          compareElement.hidden =
            true;
        }
      }
    };


  /* ============================================================
     ADD BUTTON
     ============================================================ */

  #handleRecommendationClick =
    async (event) => {
      if (
        !(
          event.target
          instanceof Element
        )
      ) {
        return;
      }


      const button =
        /** @type {HTMLButtonElement | null} */ (
          event.target.closest(
            '[data-endure-recommendation-add]'
          )
        );


      if (
        !button ||
        !this.contains(button) ||
        button.disabled
      ) {
        return;
      }


      event.preventDefault();


      const card =
        button.closest(
          '[data-endure-rec-card]'
        );


      const select =
        /** @type {HTMLSelectElement | null} */ (
          card?.querySelector(
            '[data-endure-rec-variant]'
          )
        );


      const variantId =
        select?.value ||
        button.dataset.variantId;


      if (!variantId) {
        return;
      }


      const originalText =
        button.textContent ||
        'أضف للسلة';


      button.disabled =
        true;


      button.classList.remove(
        'is-added'
      );


      button.textContent =
        'جاري الإضافة...';


      const status =
        /** @type {HTMLElement | null} */ (
          this.querySelector(
            '[data-endure-recommendations-status]'
          )
        );


      if (status) {
        status.textContent =
          '';
      }


      try {
        await this.#addVariantToCart(
          variantId,
          button
        );


        if (button.isConnected) {
          button.classList.add(
            'is-added'
          );

          button.textContent =
            'تمت الإضافة ✓';
        }


        this.#lastRecommendationSignature =
          '';


        this.#queueRecommendationsLoad(
          true
        );
      } catch (error) {
        console.error(
          '[ENDURE Cart Recommendations]',
          error
        );


        if (button.isConnected) {
          button.disabled =
            false;

          button.classList.remove(
            'is-added'
          );

          button.textContent =
            originalText;
        }


        if (status?.isConnected) {
          status.textContent =
            error instanceof Error
              ? error.message
              : 'تعذر إضافة المنتج للسلة';
        }
      }
    };


  /* ============================================================
     ADD TO CART
     ============================================================ */

  async #addVariantToCart(
    variantId,
    eventTarget
  ) {
    const deferred =
      CartLinesUpdateEvent
        .createPromise();


    /*
      Dispatch before cart request.
    */
    eventTarget.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',

        context: 'cart',

        lines: [
          {
            merchandiseId:
              String(
                variantId
              ),

            quantity: 1
          }
        ],

        promise:
          deferred.promise,

        detail: {
          source:
            'endure-cart-recommendations'
        }
      })
    );


    try {
      const root =
        window.Shopify?.routes?.root ||
        '/';


      /* =========================
         ADD PRODUCT
         ========================= */

      const response =
        await fetch(
          `${root}cart/add.js`,
          {
            method: 'POST',

            headers: {
              'Content-Type':
                'application/json',

              Accept:
                'application/json',

              'X-Requested-With':
                'XMLHttpRequest'
            },

            body:
              JSON.stringify({
                items: [
                  {
                    id:
                      Number(
                        variantId
                      ),

                    quantity: 1
                  }
                ],

                sections: [
                  'cart-drawer-section'
                ],

                sections_url:
                  window.location.pathname
              })
          }
        );


      const addResult =
        await response.json();


      if (!response.ok) {
        throw new Error(
          addResult?.description ||
          addResult?.message ||
          'تعذر إضافة المنتج للسلة'
        );
      }


      /* =========================
         UPDATED CART
         ========================= */

      const cartResponse =
        await fetch(
          `${root}cart.js`,
          {
            headers: {
              Accept:
                'application/json'
            }
          }
        );


      if (!cartResponse.ok) {
        throw new Error(
          'تعذر تحديث السلة'
        );
      }


      const ajaxCart =
        await cartResponse.json();


      /* =========================
         RESOLVE SHOPIFY EVENT
         ========================= */

      deferred.resolve({
        cart:
          CartLinesUpdateEvent
            .createCartFromAjaxResponse(
              ajaxCart
            ),

        detail: {
          items:
            addResult?.items ||
            [],

          source:
            'product-form-component',

          sourceId:
            'endure-cart-recommendations',

          itemCount: 1,

          sections:
            addResult?.sections ||
            {},

          didError:
            false
        }
      });
    } catch (error) {
      deferred.reject(
        error
      );

      throw error;
    }
  }


  /* ============================================================
     MONEY
     ============================================================ */

  #formatMoney(
    value
  ) {
    const cents =
      Number(
        value ||
        0
      );


    const currency =
      window.Shopify
        ?.currency
        ?.active ||
      'EGP';


    const language =
      document
        .documentElement
        .lang ||
      'ar-EG';


    try {
      return new Intl.NumberFormat(
        language,
        {
          style: 'currency',
          currency,
          maximumFractionDigits: 2
        }
      ).format(
        cents / 100
      );
    } catch {
      return `${(
        cents / 100
      ).toFixed(2)} ${currency}`;
    }
  }


  /* ============================================================
     ESCAPE
     ============================================================ */

  #escapeHtml(
    value
  ) {
    const div =
      document.createElement(
        'div'
      );


    div.textContent =
      String(
        value ??
        ''
      );


    return div.innerHTML;
  }


  #escapeAttribute(
    value
  ) {
    return this
      .#escapeHtml(
        value
      )
      .replace(
        /"/g,
        '&quot;'
      );
  }


  /* ============================================================
     CSS
     ============================================================ */

  #ensureRecommendationStyles() {
    if (
      document.getElementById(
        'endure-cart-recommendations-styles'
      )
    ) {
      return;
    }


    const style =
      document.createElement(
        'style'
      );


    style.id =
      'endure-cart-recommendations-styles';


    style.textContent = `

      /* =========================================================
         ENDURE CART RECOMMENDATIONS
         ========================================================= */

      .endure-cart-recommendations {
        width: 100%;
        box-sizing: border-box;

        padding:
          18px
          var(--cart-drawer-padding)
          20px;

        border-top:
          1px solid
          rgba(17,17,17,.10);

        background:
          var(--color-background);

        color:
          #111;

        direction:
          rtl;
      }


      .endure-cart-recommendations[hidden] {
        display:
          none !important;
      }


      /* =========================================================
         EMPTY CART
         ========================================================= */

      .cart-drawer--empty
      .cart-drawer__content {
        justify-content:
          flex-start !important;

        overflow-y:
          auto !important;
      }


      .cart-drawer--empty
      .endure-cart-recommendations {
        margin-top:
          12px;

        border-top:
          1px solid
          rgba(17,17,17,.10);
      }


      /* =========================================================
         HEADER
         ========================================================= */

      .endure-cart-recommendations__header {
        display:
          flex;

        align-items:
          center;

        justify-content:
          space-between;

        margin:
          0 0 12px;
      }


      .endure-cart-recommendations__title {
        margin:
          0;

        color:
          #111;

        font-size:
          14px;

        font-weight:
          700;

        line-height:
          1.35;
      }


      .endure-cart-recommendations__subtitle {
        margin:
          3px 0 0;

        color:
          rgba(17,17,17,.58);

        font-size:
          10px;

        font-weight:
          400;

        line-height:
          1.45;
      }


      /* =========================================================
         TRACK
         ========================================================= */

      .endure-cart-recommendations__track {
        display:
          flex;

        gap:
          10px;

        width:
          100%;

        overflow-x:
          auto;

        overflow-y:
          hidden;

        padding:
          0 0 5px;

        margin:
          0;

        scroll-snap-type:
          x proximity;

        overscroll-behavior-inline:
          contain;

        -webkit-overflow-scrolling:
          touch;

        scrollbar-width:
          none;
      }


      .endure-cart-recommendations__track::-webkit-scrollbar {
        display:
          none;
      }


      /* =========================================================
         CARD
         ========================================================= */

      .endure-cart-rec-card {
        flex:
          0 0 148px;

        width:
          148px;

        min-width:
          148px;

        overflow:
          hidden;

        scroll-snap-align:
          start;

        border:
          1px solid
          rgba(17,17,17,.12);

        border-radius:
          10px;

        background:
          #fff;

        color:
          #111;
      }


      /* =========================================================
         IMAGE
         ========================================================= */

      .endure-cart-rec-card__image-link {
        display:
          block;

        width:
          100%;

        text-decoration:
          none;

        background:
          #f7f7f7;
      }


      .endure-cart-rec-card__image-wrap {
        display:
          block;

        width:
          100%;

        aspect-ratio:
          4 / 5;

        overflow:
          hidden;

        background:
          #f7f7f7;
      }


      .endure-cart-rec-card__image,
      .endure-cart-rec-card__image-placeholder {
        display:
          block;

        width:
          100%;

        height:
          100%;
      }


      .endure-cart-rec-card__image {
        object-fit:
          cover;

        object-position:
          center;
      }


      .endure-cart-rec-card__image-placeholder {
        background:
          #eee;
      }


      /* =========================================================
         BODY
         ========================================================= */

      .endure-cart-rec-card__body {
        padding:
          10px;
      }


      .endure-cart-rec-card__title {
        display:
          -webkit-box;

        min-height:
          34px;

        margin:
          0 0 7px;

        overflow:
          hidden;

        color:
          #111;

        font-size:
          12px;

        font-weight:
          600;

        line-height:
          1.4;

        text-decoration:
          none;

        -webkit-box-orient:
          vertical;

        -webkit-line-clamp:
          2;
      }


      /* =========================================================
         PRICE
         ========================================================= */

      .endure-cart-rec-card__price-row {
        display:
          flex;

        align-items:
          baseline;

        flex-wrap:
          wrap;

        gap:
          5px;

        min-height:
          20px;

        margin:
          0 0 8px;
      }


      .endure-cart-rec-card__price {
        color:
          #111;

        font-size:
          12px;

        font-weight:
          700;

        line-height:
          1.2;
      }


      .endure-cart-rec-card__compare {
        color:
          #d60000;

        font-size:
          10px;

        font-weight:
          700;

        line-height:
          1.2;

        opacity:
          1;

        text-decoration-color:
          #d60000;
      }


      /* =========================================================
         SELECT
         ========================================================= */

      .endure-cart-rec-card__select {
        width:
          100%;

        min-height:
          34px;

        margin:
          0 0 7px;

        padding:
          6px 8px;

        box-sizing:
          border-box;

        border:
          1px solid
          rgba(17,17,17,.18);

        border-radius:
          6px;

        background:
          #fff;

        color:
          #111;

        font:
          inherit;

        font-size:
          11px;

        line-height:
          1.2;

        direction:
          rtl;

        cursor:
          pointer;
      }


      /* =========================================================
         ADD BUTTON
         ========================================================= */

      .endure-cart-rec-card__add {
        width:
          100%;

        min-height:
          36px;

        padding:
          7px 8px;

        box-sizing:
          border-box;

        border:
          1px solid
          #111;

        border-radius:
          6px;

        background:
          #111;

        color:
          #fff;

        font:
          inherit;

        font-size:
          11px;

        font-weight:
          700;

        line-height:
          1.2;

        cursor:
          pointer;

        transition:
          opacity .2s ease,
          background .2s ease,
          border-color .2s ease;
      }


      .endure-cart-rec-card__add:hover {
        background:
          #2a2a2a;
      }


      .endure-cart-rec-card__add:disabled {
        cursor:
          wait;

        opacity:
          .62;
      }


      .endure-cart-rec-card__add.is-added {
        background:
          #5c7a3e;

        border-color:
          #5c7a3e;
      }


      /* =========================================================
         STATUS
         ========================================================= */

      .endure-cart-recommendations__status {
        margin:
          8px 0 0;

        color:
          #b42318;

        font-size:
          10px;

        line-height:
          1.4;
      }


      .endure-cart-recommendations__status:empty {
        display:
          none;
      }


      /* =========================================================
         MOBILE
         ========================================================= */

      @media screen and (max-width: 749px) {

        .endure-cart-recommendations {
          padding:
            15px
            var(--cart-drawer-padding)
            17px;
        }


        .endure-cart-recommendations__header {
          margin-bottom:
            10px;
        }


        .endure-cart-recommendations__title {
          font-size:
            13px;
        }


        .endure-cart-recommendations__subtitle {
          font-size:
            10px;
        }


        .endure-cart-rec-card {
          flex:
            0 0 142px;

          width:
            142px;

          min-width:
            142px;
        }

      }

    `;


    document.head.appendChild(
      style
    );
  }
}


if (
  !customElements.get(
    'cart-drawer-component'
  )
) {
  customElements.define(
    'cart-drawer-component',
    CartDrawerComponent
  );
}